import type { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { VariantFulfillmentMode, VariantStatus } from '@nucleus-commerce/api-client/admin';
import type { WeightUnitCode } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { cellPath, type VariantRowsInput } from '../api/variant-mappers';
import type { WeightFieldInput } from '../schemas/weight-schema';

import { WeightInput } from './weight-input';

/** An edited, unsaved cell: tinted so the person sees what Save will write. */
const DIRTY = 'border-primary bg-primary/5';
const INHERIT = '__inherit__';
const NO_WEIGHT: WeightFieldInput = { value: '', unit: '' };

/** A row can render for a moment before it is in the form; its inputs stay controlled. */
const orEmpty = (value: string | undefined) => value ?? '';
const orNoWeight = (value: WeightFieldInput | undefined) => value ?? NO_WEIGHT;

interface CellProps {
  id: string;
  /** "SKU for 14K / EF / VS1": every cell input needs its own accessible name. */
  label: string;
}

function CellError({ message }: { message: string | undefined }) {
  return message ? (
    <p role="alert" className="text-destructive max-w-56 text-xs whitespace-normal">
      {message}
    </p>
  ) : null;
}

function Stack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-1">{children}</div>;
}

export function TextCell({
  id,
  field,
  label,
  className,
  after,
}: CellProps & {
  field: 'sku' | 'barcode' | 'taxCode';
  className?: string;
  after?: ReactNode;
}) {
  const { control } = useFormContext<VariantRowsInput>();
  return (
    <Controller
      control={control}
      name={cellPath(id, field)}
      render={({ field: input, fieldState }) => (
        <Stack>
          <div className="flex min-w-0 items-center gap-1.5">
            <Input
              ref={input.ref}
              name={input.name}
              value={orEmpty(input.value)}
              onChange={input.onChange}
              onBlur={input.onBlur}
              disabled={input.disabled}
              aria-label={label}
              aria-invalid={fieldState.invalid}
              spellCheck={false}
              className={cn('h-8 min-w-0 flex-1 text-xs', className, fieldState.isDirty && DIRTY)}
            />
            {after}
          </div>
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

export function MoneyCell({
  id,
  field,
  label,
  currency,
}: CellProps & { field: 'price' | 'compareAt' | 'cost'; currency: string }) {
  const { control } = useFormContext<VariantRowsInput>();
  return (
    <Controller
      control={control}
      name={cellPath(id, field)}
      render={({ field: input, fieldState }) => (
        <Stack>
          <MoneyInput
            currency={currency}
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            disabled={input.disabled}
            aria-label={label}
            aria-invalid={fieldState.invalid}
            className={cn('h-8 w-full pl-12 text-xs', fieldState.isDirty && DIRTY)}
          />
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

/** Profit or margin from the row's current price and cost, edited or not. */
export function MarginFigure({
  id,
  currency,
  show,
}: {
  id: string;
  currency: string;
  show: 'profit' | 'margin';
}) {
  const format = useFormat();
  const { control } = useFormContext<VariantRowsInput>();
  const price = useWatch({ control, name: cellPath(id, 'price') });
  const cost = useWatch({ control, name: cellPath(id, 'cost') });
  if (!price || !cost || price.amount <= 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const profit = price.amount - cost.amount;
  return (
    <span className="tabular-nums">
      {show === 'profit'
        ? format.money({ amount: profit, currency })
        : `${Math.round((profit / price.amount) * 100)}%`}
    </span>
  );
}

export function WeightCell({
  id,
  label,
  defaultUnit,
}: CellProps & { defaultUnit: WeightUnitCode }) {
  const { control } = useFormContext<VariantRowsInput>();
  return (
    <Controller
      control={control}
      name={cellPath(id, 'weight')}
      render={({ field: input, fieldState }) => (
        <Stack>
          <div className={cn('w-full rounded-md', fieldState.isDirty && 'ring-primary ring-1')}>
            <WeightInput
              compact
              value={orNoWeight(input.value)}
              onChange={input.onChange}
              onBlur={input.onBlur}
              defaultUnit={defaultUnit}
              disabled={input.disabled}
              aria-label={label}
              aria-invalid={fieldState.invalid}
            />
          </div>
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

export function SwitchCell({
  id,
  field,
  label,
  disabledWhen,
}: CellProps & {
  field: 'taxable' | 'trackInventory' | 'allowBackorder' | 'requiresShipping';
  /** Another field of the row that turns this one off (backorders need tracked stock). */
  disabledWhen?: 'trackInventory';
}) {
  const { control } = useFormContext<VariantRowsInput>();
  const gate = useWatch({ control, name: cellPath(id, disabledWhen ?? 'trackInventory') });
  return (
    <Controller
      control={control}
      name={cellPath(id, field)}
      render={({ field: input, fieldState }) => (
        <Stack>
          <div
            className={cn(
              'inline-flex w-fit rounded-full p-0.5',
              fieldState.isDirty && 'ring-primary ring-1',
            )}
          >
            <Switch
              ref={input.ref}
              checked={input.value}
              onCheckedChange={(checked) => input.onChange(checked)}
              onBlur={input.onBlur}
              disabled={(input.disabled ?? false) || (disabledWhen !== undefined && !gate)}
              aria-label={label}
            />
          </div>
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

export function FulfillmentCell({ id, label }: CellProps) {
  const { t } = useTranslation('products');
  const { control } = useFormContext<VariantRowsInput>();
  return (
    <Controller
      control={control}
      name={cellPath(id, 'fulfillmentMode')}
      render={({ field: input, fieldState }) => (
        <Stack>
          <Select
            value={input.value || INHERIT}
            onValueChange={(value) => input.onChange(value === INHERIT ? '' : value)}
            disabled={input.disabled}
          >
            <SelectTrigger
              size="sm"
              aria-label={label}
              aria-invalid={fieldState.invalid}
              className={cn('w-full text-xs', fieldState.isDirty && DIRTY)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={INHERIT}>{t('variantsCard.fields.sameAsProduct')}</SelectItem>
              {Object.values(VariantFulfillmentMode).map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {t(`fulfillmentMode.${mode}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

export function StatusCell({ id, label }: CellProps) {
  const { t } = useTranslation('common');
  const { control } = useFormContext<VariantRowsInput>();
  return (
    <Controller
      control={control}
      name={cellPath(id, 'status')}
      render={({ field: input, fieldState }) => (
        <Stack>
          <Select value={input.value} onValueChange={input.onChange} disabled={input.disabled}>
            <SelectTrigger
              size="sm"
              aria-label={label}
              aria-invalid={fieldState.invalid}
              className={cn('w-full text-xs', fieldState.isDirty && DIRTY)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(VariantStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CellError message={fieldState.error?.message} />
        </Stack>
      )}
    />
  );
}

/** Several short number fields side by side: lead time min–max, or length × width × height. */
export function NumbersCell({
  id,
  fields,
  separator,
}: {
  id: string;
  fields: ReadonlyArray<{
    field: 'leadTimeDaysMin' | 'leadTimeDaysMax' | 'lengthMm' | 'widthMm' | 'heightMm';
    label: string;
  }>;
  separator: string;
}) {
  const { control, getFieldState, formState } = useFormContext<VariantRowsInput>();
  const errors = fields
    .map(({ field }) => getFieldState(cellPath(id, field), formState).error?.message)
    .filter((message): message is string => Boolean(message));
  return (
    <Stack>
      <div className="flex items-center gap-1">
        {fields.map(({ field, label }, index) => (
          <span key={field} className="flex min-w-0 flex-1 items-center gap-1">
            {index > 0 ? <span className="text-muted-foreground text-xs">{separator}</span> : null}
            <Controller
              control={control}
              name={cellPath(id, field)}
              render={({ field: input, fieldState }) => (
                <Input
                  ref={input.ref}
                  name={input.name}
                  value={orEmpty(input.value)}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  disabled={input.disabled}
                  inputMode="decimal"
                  aria-label={label}
                  aria-invalid={fieldState.invalid}
                  className={cn(
                    'h-8 w-full min-w-0 text-xs tabular-nums',
                    fieldState.isDirty && DIRTY,
                  )}
                />
              )}
            />
          </span>
        ))}
      </div>
      <CellError message={errors[0]} />
    </Stack>
  );
}
